import { useEffect, useState } from "react";
import { Images, Image as ImageIcon, Film, Users, UserCheck, Mail, Download, Eye, Heart, Tags, Loader, AlertTriangle, Trash2, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

// Grouped so the dashboard reads left-to-right: what you have → how it's being used → who's using it.
const CARDS = [
  // Row 1 · Content inventory
  { key: "total_ads", label: "Total Ads", icon: Images, color: "#0FFFE2" },
  { key: "images", label: "Image Ads", icon: ImageIcon, color: "#D0FF0D" },
  { key: "videos", label: "Video Ads", icon: Film, color: "#4A0A66" },
  { key: "processing", label: "Processing", icon: Loader, color: "#FC861E" },
  // Row 2 · Engagement
  { key: "total_views", label: "Total Views", icon: Eye, color: "#4A0A66" },
  { key: "total_downloads", label: "Downloads", icon: Download, color: "#0FFFE2" },
  { key: "favorites", label: "Favorites Saved", icon: Heart, color: "#DF1283" },
  { key: "new_this_week", label: "New This Week", icon: Sparkles, color: "#D0FF0D" },
  // Row 3 · People / operations
  { key: "clients", label: "Clients", icon: Users, color: "#DF1283" },
  { key: "active_clients", label: "Active Clients", icon: UserCheck, color: "#0FFFE2" },
  { key: "expiring_soon", label: "Expiring ≤7d", icon: Clock, color: "#FC861E" },
  { key: "pending_invites", label: "Pending Invites", icon: Mail, color: "#4A0A66" },
];

const SCOPES = [
  { key: "include_ads", label: "All ads & their analytics", desc: "Ads, per-ad view/download counters, favorites, view/download events" },
  { key: "include_clients", label: "All clients & invites", desc: "Client accounts, pending invites, password resets, lockouts" },
  { key: "include_access_codes", label: "All access codes", desc: "Access codes and their usage history" },
  { key: "include_analytics", label: "Analytics only (if ads are kept)", desc: "Zeroes view/download counters and clears event history — no effect on top of the ads reset" },
];

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [showReset, setShowReset] = useState(false);
  const [preview, setPreview] = useState(null);
  const [scope, setScope] = useState({ include_ads: true, include_clients: true, include_access_codes: true, include_analytics: true });
  const [confirmation, setConfirmation] = useState("");
  const [wiping, setWiping] = useState(false);

  const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => setStats({}));
  useEffect(() => { load(); }, []);

  const openReset = async () => {
    setShowReset(true);
    setPreview(null);
    setConfirmation("");
    try {
      const { data } = await api.get("/admin/reset-data/preview");
      setPreview(data);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const doReset = async () => {
    if (confirmation !== "RESET") {
      toast.error("Type RESET to confirm");
      return;
    }
    setWiping(true);
    try {
      const { data } = await api.post("/admin/reset-data", { confirmation, ...scope });
      const bits = Object.entries(data.wiped).filter(([, v]) => v > 0).map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`);
      toast.success(bits.length ? `Wiped ${bits.join(", ")}` : "Nothing to wipe");
      setShowReset(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setWiping(false);
    }
  };

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-3xl text-[#000000]">Admin Dashboard</h1>
          <p className="text-gray-500 mt-1 text-sm">Your lookbook at a glance.</p>
        </div>
        <button
          data-testid="open-reset-modal-btn"
          onClick={openReset}
          className="flex items-center gap-2 bg-white border border-gray-200 text-gray-600 rounded-full px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] hover:bg-[#DF1283] hover:text-white hover:border-[#DF1283] transition-colors"
        >
          <Trash2 size={13} /> Reset Demo Data
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mt-8" data-testid="admin-stats-grid">
        {CARDS.map(({ key, label, icon: Icon, color }) => (
          <div key={key} className="bg-white border border-gray-200 rounded-xl p-6" data-testid={`stat-${key}`}>
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: `${color}22` }}>
              <Icon size={17} style={{ color: color === "#D0FF0D" ? "#000000" : color }} />
            </div>
            <div className="font-display font-black text-3xl text-[#000000] mt-4">{stats ? (stats[key] ?? 0) : "—"}</div>
            <div className="text-xs font-bold uppercase tracking-[0.1em] text-gray-400 mt-1">{label}</div>
          </div>
        ))}
      </div>

      <Dialog open={showReset} onOpenChange={setShowReset}>
        <DialogContent className="max-w-lg" data-testid="reset-data-modal">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <AlertTriangle size={20} className="text-[#DF1283]" /> Reset demo data
            </DialogTitle>
            <DialogDescription className="text-xs text-gray-500">
              Wipes the selected data so you can onboard real clients on a clean slate. Your admin account and categories are always preserved. <strong className="text-[#DF1283]">This cannot be undone.</strong>
            </DialogDescription>
          </DialogHeader>

          {!preview ? (
            <div className="py-8 text-center text-sm text-gray-400">Loading current data…</div>
          ) : (
            <>
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500 mb-2">Currently in your database</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Ads</span><span className="font-mono font-bold text-[#000000]" data-testid="reset-preview-ads">{preview.ads}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Clients</span><span className="font-mono font-bold text-[#000000]" data-testid="reset-preview-clients">{preview.clients}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Pending invites</span><span className="font-mono font-bold text-[#000000]">{preview.pending_invites}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Access codes</span><span className="font-mono font-bold text-[#000000]">{preview.access_codes}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Favorites</span><span className="font-mono font-bold text-[#000000]">{preview.favorites}</span></div>
                  <div className="flex justify-between"><span className="text-gray-500">Analytics events</span><span className="font-mono font-bold text-[#000000]" data-testid="reset-preview-events">{preview.analytics_events}</span></div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">What to wipe</div>
                {SCOPES.map((s) => (
                  <label key={s.key} className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:border-[#DF1283] cursor-pointer transition-colors">
                    <input
                      type="checkbox"
                      data-testid={`reset-scope-${s.key}`}
                      checked={scope[s.key]}
                      onChange={(e) => setScope({ ...scope, [s.key]: e.target.checked })}
                      className="mt-1 accent-[#DF1283]"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-bold text-[#000000]">{s.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{s.desc}</div>
                    </div>
                  </label>
                ))}
              </div>

              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-500">Type <span className="font-mono text-[#DF1283]">RESET</span> to confirm</label>
                <input
                  data-testid="reset-confirmation-input"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder="RESET"
                  className="mt-1.5 w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#DF1283]"
                />
              </div>

              <div className="flex gap-2">
                <button
                  data-testid="reset-cancel-btn"
                  onClick={() => setShowReset(false)}
                  className="flex-1 bg-gray-100 text-[#000000] rounded-full px-6 py-2.5 font-bold text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="reset-confirm-btn"
                  onClick={doReset}
                  disabled={confirmation !== "RESET" || wiping}
                  className="flex-1 bg-[#DF1283] text-white rounded-full px-6 py-2.5 font-bold text-sm hover:bg-[#000000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {wiping ? "Wiping…" : "Wipe the data"}
                </button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
